// Import external
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Import internal
import { ErrorService } from '@app/services/error.service';
import { FilterParams } from '@claranet/node-search-filter';
import {
    AssignmentGroup,
    EmbedAssignmentGroup,
} from '@data/models/assignment-group.model';
import { Embedded } from '@data/models/embed.model';
import { Inventory } from '@data/models/inventory.model';
import { Stat } from '@data/models/stat';
import {
    configuration as ticketConfiguration,
    embed as ticketEmbed,
    EmbedTicket,
    Ticket,
} from '@data/models/ticket.model';
import { ProjectService } from '@data/services/project.service';
import { toParams } from '@shared/utils/query-to-params';
import * as _ from 'lodash';
import { Observable, lastValueFrom } from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';

export const TICKET_SERVICE_API_ROOT = '/ticketing/v1/projects';
export const ONE_SERVICE_API_ROOT = '/one/v1/ticketing';

@Injectable()
export class TicketService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private projectService: ProjectService,
    ) {
        this.errors = this.errorService.ticketServiceErrors;
    }

    public findTickets(filterParams: FilterParams): Promise<Ticket[]> {
        const params = toParams(filterParams);
        return lastValueFrom(this.http.get<{ tickets: Ticket[] }>(`${TICKET_SERVICE_API_ROOT}/-/tickets`, { params }).pipe(
            map((result) => result.tickets),
            catchError(this.errorService.errorHandler<Ticket[]>(this.errors, 'Récupération des tickets', []))
        ));
    }

    public listEmbededTickets<T extends ticketEmbed>(filterParams: FilterParams, embedFields: T[] = []): Observable<{ tickets: EmbedTicket<T>[]; totalSize: number }> {
        const embeds = _.uniq(embedFields.map((it) => ticketConfiguration[it].embed)).filter(Boolean);
        const params = toParams({ ...filterParams, embed: !_.isEmpty(embeds) && embeds.join(',') });
        return this.http.get<{ tickets: Ticket[]; totalSize: number; embedded: Embedded }>(`${TICKET_SERVICE_API_ROOT}/-/tickets`, { params }).pipe(
            map(({ tickets, totalSize, embedded }) => ({ tickets: Ticket.toCustomTicketList(tickets, embedded, embedFields), totalSize })),
            catchError(
                this.errorService.errorHandler<{ tickets: EmbedTicket<T>[]; totalSize: number; embedded: Embedded }>(
                    this.errors, 'Récupération des tickets', { tickets: [], embedded: {}, totalSize: 0 })
            )
        );
    }

    public getTicket<T extends ticketEmbed>(
        { projectId, ticketId, embedFields = [] }: { projectId: string; ticketId: string; embedFields: T[] }): Observable<EmbedTicket<T>> {
        const embeds = _.uniq(embedFields.map((it) => ticketConfiguration[it].embed)).filter(Boolean).join(',');
        const params = toParams({ embed: embeds });
        return this.http.get<Ticket & { embedded: Embedded }>(`${TICKET_SERVICE_API_ROOT}/${projectId}/tickets/${ticketId}`, { params }).pipe(
            map(({ embedded, ...result }) => Ticket.toCustomTicket(result, embedded, embedFields)),
            catchError(
                this.errorService.errorHandler<EmbedTicket<T>>(this.errors, 'Récupération du ticket', null)
            )
        );
    }

    public async getProjectsByInventory(inventory: Inventory) {
        const projectIds: string[] = _.uniq(_.compact(inventory.projects));
        return projectIds.length ?
            await lastValueFrom(this.projectService.findProjects({ filter: `id:(${projectIds.join('|')})` }))
            : [];
    }

    public computeStats(filterParams: FilterParams & { aggregate?: string; groupBy?: string }, projectId?: string): Promise<Stat[]> {
        const params = toParams(filterParams);
        return lastValueFrom(this.http.get<{ stats: Stat[] }>(`${TICKET_SERVICE_API_ROOT}/${projectId ? projectId : '-'}/stats`, { params }).pipe(
            map((result) => result.stats),
            catchError(
                this.errorService.errorHandler<Stat[]>(this.errors, 'Récupération des stats', [])
            )
        ));
    }

    public listAssignmentGroups(filterParams: FilterParams): Observable<EmbedAssignmentGroup[]> {
        const params = toParams(filterParams);
        return this.http.get<{ assignmentGroups: AssignmentGroup[] }>(`${ONE_SERVICE_API_ROOT}/sources/-/assignment-groups`, { params }).pipe(
            map(({ assignmentGroups }) => AssignmentGroup.toCustomAssignmentGroupList(assignmentGroups)),
            catchError(
                this.errorService.errorHandler<EmbedAssignmentGroup[]>(this.errors, 'Récupération des groupes d\'assignement', [])
            )
        );
    }
}
